import { useCallback, useEffect, useState } from 'react';
import { diaryApi, type DiaryEvent, type DiaryKind, type NewDiaryEvent } from '../../api';
import Select from '../common/Select';
import DeadlineCalculator from '../common/DeadlineCalculator';

type Props = { matterId: number | string };

const KINDS: Array<{ value: DiaryKind; label: string }> = [
  { value: 'court_date',   label: 'Court date' },
  { value: 'deadline',     label: 'Deadline' },
  { value: 'consultation', label: 'Consultation' },
  { value: 'conference',   label: 'Conference' },
  { value: 'reminder',     label: 'Reminder' },
];

const SEVERITIES = [
  { value: 'info',      label: 'Info' },
  { value: 'normal',    label: 'Normal' },
  { value: 'warning',   label: 'Warning' },
  { value: 'critical',  label: 'Critical' },
  { value: 'statutory', label: 'Statutory' },
];

export default function DiaryPanel({ matterId }: Props) {
  const [events, setEvents] = useState<DiaryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState<string | true | false>(false); // string = pre-filled date
  const [showCalc, setShowCalc] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const to = new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10);
      const r = await diaryApi.list({ matter_id: matterId, from: today, to });
      setEvents(r.events || []);
    } finally { setLoading(false); }
  }, [matterId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id: number | string) => {
    if (!confirm('Delete this diary event?')) return;
    await diaryApi.remove(id);
    load();
  };

  return (
    <div className="stack">
      <div className="row">
        <h3 className="grow" style={{ margin: 0 }}>Diary</h3>
        <button onClick={() => setShowCalc((s) => !s)}>
          {showCalc ? '− Hide deadline calculator' : '+ Deadline calculator'}
        </button>
        <button className="primary" onClick={() => setAdding(true)}>+ New event</button>
      </div>

      {showCalc && (
        <DeadlineCalculator
          onUse={(iso) => { setAdding(iso); setShowCalc(false); }}
        />
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 18 }} className="muted">Loading…</div>
        ) : events.length === 0 ? (
          <div style={{ padding: 18 }} className="muted">
            No upcoming diary events on this matter. Add a court date, deadline, or consultation above.
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {events.map((ev) => {
              const isCritical = ev.severity === 'critical' || ev.severity === 'statutory';
              return (
                <li key={String(ev.ROWID)} style={row}>
                  <div style={{
                    width: 4, alignSelf: 'stretch', borderRadius: 2,
                    background: isCritical ? 'var(--color-danger)' : 'var(--color-accent)',
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500 }}>{ev.title}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {formatWhen(ev.starts_at, ev.ends_at)}
                      {ev.statutory_reference ? ` · ${ev.statutory_reference}` : ''}
                      {ev.location ? ` · ${ev.location}` : ''}
                    </div>
                  </div>
                  <span className={`pill ${isCritical ? 'danger' : ''}`}>{ev.kind.replace('_', ' ')}</span>
                  <button
                    className="ghost"
                    style={{ padding: '4px 8px', color: 'var(--color-danger)' }}
                    onClick={() => remove(ev.ROWID)}
                    title="Delete"
                  >×</button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {adding && (
        <NewDiaryModal
          matterId={matterId}
          prefillStartDate={typeof adding === 'string' ? adding : undefined}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); load(); }}
        />
      )}
    </div>
  );
}

function NewDiaryModal({ matterId, prefillStartDate, onClose, onSaved }: { matterId: number | string; prefillStartDate?: string; onClose: () => void; onSaved: () => void }) {
  const defaultStart = prefillStartDate
    // Prefilled from calculator — default to 09:00 on the computed date.
    ? `${prefillStartDate}T09:00`
    : (() => {
        const inOneHour = new Date(Date.now() + 3600000);
        inOneHour.setMinutes(0, 0, 0);
        return inOneHour.toISOString().slice(0, 16);
      })();
  const [form, setForm] = useState<NewDiaryEvent>({
    matter_id: matterId,
    kind: 'deadline',
    title: '',
    starts_at: defaultStart,
    severity: 'normal',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    if (!form.starts_at)    { setError('Start date/time is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      // Append seconds and treat input as local time — server stores as-is
      const starts = form.starts_at.length === 16 ? form.starts_at + ':00' : form.starts_at;
      const ends = form.ends_at ? (form.ends_at.length === 16 ? form.ends_at + ':00' : form.ends_at) : undefined;
      await diaryApi.create({
        ...form,
        starts_at: starts,
        ends_at: ends,
        description: form.description || undefined,
        statutory_reference: form.statutory_reference || undefined,
        location: form.location || undefined,
      });
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div className="card" style={modalCard} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: 14 }}>New diary event</h3>
        <div className="stack">
          <div className="row">
            <div style={{ flex: 1 }}>
              <label>Kind</label>
              <Select
                value={form.kind}
                onChange={(v) => setForm({ ...form, kind: v as DiaryKind })}
                options={KINDS}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label>Severity</label>
              <Select
                value={form.severity || 'normal'}
                onChange={(v) => setForm({ ...form, severity: v })}
                options={SEVERITIES}
              />
            </div>
          </div>
          <div>
            <label>Title</label>
            <input autoFocus value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="row">
            <div style={{ flex: 1 }}>
              <label>Starts</label>
              <input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} />
            </div>
            <div style={{ flex: 1 }}>
              <label>Ends (optional)</label>
              <input type="datetime-local" value={form.ends_at || ''} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} />
            </div>
          </div>
          <div>
            <label>Location (optional)</label>
            <input value={form.location || ''} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </div>
          <div>
            <label>Statutory reference (optional)</label>
            <input placeholder="e.g. Uniform Rule 22(1)" value={form.statutory_reference || ''} onChange={(e) => setForm({ ...form, statutory_reference: e.target.value })} />
          </div>
          <div>
            <label>Description (optional)</label>
            <textarea rows={2} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button onClick={onClose} disabled={saving}>Cancel</button>
            <button className="primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save event'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatWhen(starts: string, ends?: string | null) {
  try {
    const d = new Date(starts);
    const fmt = d.toLocaleString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    if (!ends) return fmt;
    const e = new Date(ends);
    const sameDay = d.toDateString() === e.toDateString();
    return `${fmt} – ${sameDay ? e.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }) : e.toLocaleString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`;
  } catch { return starts; }
}

const row: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '12px 18px', borderBottom: '1px solid var(--color-border)',
};
const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
};
const modalCard: React.CSSProperties = { width: 560, maxWidth: '90vw' };
